<template>
  <div class="kiosk-step-scheduling">
    <div class="scheduling-container">
      <!-- Scrollable Content -->
      <div class="scroll-body">
        <h2 class="step-title">
          When would you like your order?
        </h2>

        <!-- ASAP / Schedule Toggle -->
        <div class="toggle-section">
          <button
            class="toggle-option"
            :class="{ active: pickupSettings === 'now' }"
            @click="pickupSettings = 'now'"
          >
            <div
              class="toggle-icon-wrapper"
              :class="{ active: pickupSettings === 'now' }"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-clock"
                aria-hidden="true"
              >
                <path d="M12 6v6l4 2" />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                />
              </svg>
            </div>
            <div class="toggle-text">
              <h4
                class="toggle-title"
                :class="{ active: pickupSettings === 'now' }"
              >
                Prepare Immediately
              </h4>
              <p class="toggle-subtitle">
                We'll start making your order right away.
              </p>
            </div>
            <div
              class="toggle-radio"
              :class="{ active: pickupSettings === 'now' }"
            >
              <div
                v-if="pickupSettings === 'now'"
                class="radio-dot"
              />
            </div>
          </button>
          <button
            v-if="isSchedulingEnabled"
            class="toggle-option"
            :class="{ active: pickupSettings === 'later' }"
            @click="pickupSettings = 'later'"
          >
            <div
              class="toggle-icon-wrapper"
              :class="{ active: pickupSettings === 'later' }"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-calendar"
                aria-hidden="true"
              >
                <path d="M8 2v4" />
                <path d="M16 2v4" />
                <rect
                  width="18"
                  height="18"
                  x="3"
                  y="4"
                  rx="2"
                />
                <path d="M3 10h18" />
              </svg>
            </div>
            <div class="toggle-text">
              <h4
                class="toggle-title"
                :class="{ active: pickupSettings === 'later' }"
              >
                Schedule for Later
              </h4>
              <p class="toggle-subtitle">
                Choose a pickup time for today.
              </p>
            </div>
            <div
              class="toggle-radio"
              :class="{ active: pickupSettings === 'later' }"
            >
              <div
                v-if="pickupSettings === 'later'"
                class="radio-dot"
              />
            </div>
          </button>
        </div>

        <!-- Date/Time Selection -->
        <div
          v-if="pickupSettings === 'later'"
          class="selection-section"
        >
          <!-- Day Selection -->
          <div class="section-title">
            Select Day
          </div>
          <div class="day-grid">
            <div
              v-for="day in availableDays"
              :key="day.value"
              class="day-card"
              :class="{ selected: selectedDay === day.value }"
              @click="selectDay(day.value)"
            >
              <div class="day-name">
                {{ day.label }}
              </div>
              <div class="day-date">
                {{ day.dateLabel }}
              </div>
            </div>
          </div>

          <!-- Time Selection -->
          <h4 class="section-title">
            Select Pickup Time
          </h4>
          <div class="time-scroll">
            <div class="time-grid">
              <button
                v-for="time in availableTimeSlots"
                :key="time.value"
                class="time-card"
                :class="{ selected: selectedTime === time.value }"
                @click="selectTime(time.value)"
              >
                {{ time.label }}
              </button>
            </div>
          </div>

          <!-- Summary Bar -->
          <div
            class="summary-bar"
            v-if="selectedDay && selectedTime"
          >
            <div class="summary-label">
              Pickup scheduled for:
            </div>
            <div class="summary-value">
              {{ formattedSchedule }}
            </div>
          </div>
        </div>
      </div>

      <!-- Key Footer Buttons -->
      <div class="action-buttons">
        <button
          class="confirm-btn"
          :class="{
            disabled:
              pickupSettings === 'later' && (!selectedDay || !selectedTime),
          }"
          @click="confirmSelection"
          :disabled="
            pickupSettings === 'later' && (!selectedDay || !selectedTime)
          "
        >
          <span>Enter Details</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "StepScheduling",
  props: {
    diningOption: {
      type: Object,
      default: null,
    },
    initialPickupSettings: {
      type: String,
      default: "now",
    },
    initialServeAt: {
      type: [String, Date],
      default: null,
    },
  },
  data() {
    return {
      pickupSettings: this.initialPickupSettings,
      selectedDay: null,
      selectedTime: null,
      interval: null,
    };
  },
  computed: {
    isSchedulingEnabled() {
      return (
        this.diningOption && this.diningOption.is_scheduling_enabled === true
      );
    },
    availableDays() {
      const days = [];

      for (let i = 0; i < 4; i++) {
        const date = moment().add(i, "days");
        let label;

        if (i === 0) {
          label = "Today";
        } else if (i === 1) {
          label = "Tomorrow";
        } else {
          label = date.format("dddd");
        }

        // Check if this day is available based on dining option timings
        const dayTiming = this.getTimingForDate(date);
        const isClosed = dayTiming && dayTiming.is_closed;
        if (isClosed) continue;

        // Only show a day if it has at least one available time slot
        const hasAvailableSlots = this.getTimeSlotsForDate(date).length > 0;
        if (!hasAvailableSlots) continue;

        days.push({
          value: date.format("YYYY-MM-DD"),
          label: label,
          dateLabel: date.format("MMM D"),
        });
      }

      return days;
    },
    availableTimeSlots() {
      if (!this.selectedDay) return [];
      return this.getTimeSlotsForDate(moment(this.selectedDay));
    },
    formattedSchedule() {
      if (!this.selectedDay || !this.selectedTime) return "";
      const dateTime = moment(
        `${this.selectedDay} ${this.selectedTime}`,
        "YYYY-MM-DD HH:mm:ss",
      );
      return dateTime.format("dddd, MMMM D [at] h:mm A");
    },
    serveAtNew() {
      if (this.pickupSettings === "now") return null;
      if (!this.selectedDay || !this.selectedTime) return null;
      return new Date(`${this.selectedDay}T${this.selectedTime}`).toISOString();
    },
  },
  watch: {
    pickupSettings() {
      this.emitUpdate();
    },
    selectedDay() {
      this.emitUpdate();
    },
    selectedTime() {
      this.emitUpdate();
    },
  },
  created() {
    this.initSelection();
  },
  beforeDestroy() {
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    getTimingForDate(date) {
      if (!this.diningOption || !this.diningOption.timings) return null;

      // moment().isoWeekday() returns 1 (Monday) to 7 (Sunday)
      const dayOfWeek = date.isoWeekday();

      return this.diningOption.timings.find((t) => t.day_of_week === dayOfWeek);
    },
    getTimeSlotsForDate(date) {
      const slots = [];
      const isToday =
        date.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
      const minTime = moment().add(30, "minutes");

      // Get timing for selected day
      const dayTiming = this.getTimingForDate(date);

      // Default hours if no timing available
      let startHour = 10;
      let startMinute = 0;
      let endHour = 21;
      let endMinute = 0;

      if (dayTiming && dayTiming.opening_time && dayTiming.closing_time) {
        const opening = moment(dayTiming.opening_time, "HH:mm:ss");
        const closing = moment(dayTiming.closing_time, "HH:mm:ss");
        startHour = opening.hour();
        startMinute = opening.minute();
        endHour = closing.hour();
        endMinute = closing.minute();
      }

      // Generate 30-minute slots (inclusive of end time)
      let currentSlot = moment(date)
        .hour(startHour)
        .minute(startMinute)
        .second(0);
      const endSlot = moment(date).hour(endHour).minute(endMinute).second(0);

      while (currentSlot.isBefore(endSlot) || currentSlot.isSame(endSlot)) {
        // For today: hide past times (instead of disabling them)
        if (!isToday || currentSlot.isSameOrAfter(minTime)) {
          slots.push({
            value: currentSlot.format("HH:mm:ss"),
            label: currentSlot.format("HH:mm"),
          });
        }

        currentSlot.add(30, "minutes");
      }

      return slots;
    },
    initSelection() {
      // If we have an initialServeAt, try to parse it
      if (this.initialServeAt) {
        const dateTime = moment(this.initialServeAt);
        if (dateTime.isValid()) {
          this.selectedDay = dateTime.format("YYYY-MM-DD");
          this.selectedTime = dateTime.format("HH:mm:ss");
          return;
        }
      }

      // Default to first available day
      this.$nextTick(() => {
        if (this.availableDays.length > 0) {
          this.selectedDay = this.availableDays[0].value;
        } else {
          this.selectedDay = moment().format("YYYY-MM-DD");
        }

        // Default to first available time slot
        this.$nextTick(() => {
          if (this.availableTimeSlots.length > 0) {
            this.selectedTime = this.availableTimeSlots[0].value;
          }
        });
      });
    },
    selectDay(day) {
      this.selectedDay = day;
      // Reset time to first available slot for the new day
      this.$nextTick(() => {
        if (this.availableTimeSlots.length > 0) {
          this.selectedTime = this.availableTimeSlots[0].value;
        }
      });
    },
    selectTime(time) {
      this.selectedTime = time;
    },
    emitUpdate() {
      this.$emit("update", {
        serveAt: this.serveAtNew,
        pickupSettings: this.pickupSettings,
      });
    },
    confirmSelection() {
      this.$emit("next", {
        serveAt: this.serveAtNew,
        pickupSettings: this.pickupSettings,
      });
    },
  },
};
</script>

<style scoped>
.kiosk-step-scheduling {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.scheduling-container {
  padding: 1.5rem;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden; /* Prevent body scroll, handle via scroll-body */
}

/* Scroll Content */
.scroll-body {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 5px;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #111827;
}

/* Toggle Section */
.toggle-section {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

.toggle-option {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.toggle-option:hover {
  border-color: #d1d5db;
}

.toggle-option.active {
  border-color: #2563eb;
  background: rgba(239, 246, 255, 0.5);
}

.toggle-icon-wrapper {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.2s;
}

.toggle-icon-wrapper.active {
  background: #2563eb;
  color: white;
}

.toggle-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.toggle-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.4;
}

.toggle-title.active {
  color: #2563eb;
}

.toggle-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.toggle-radio {
  width: 24px;
  height: 24px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}

.toggle-radio.active {
  border-color: #2563eb;
}

.radio-dot {
  width: 12px;
  height: 12px;
  background: #2563eb;
  border-radius: 50%;
}

/* Selection Section */
.selection-section {
  background: #fff;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  /* Removed flex: 1 and overflow hidden to allow full height expansion within scroll body */
  min-height: 0;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
}

/* Day Grid - 4 columns for single row */
.day-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.day-card {
  padding: 1.25rem 0.75rem;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.day-card:hover {
  border-color: #b8daff;
}

.day-card.selected {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.day-name {
  font-weight: 700;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.day-date {
  font-size: 1rem;
  opacity: 0.9;
}

/* Time Grid */
.time-scroll {
  /* Removed scroll/flex constraints so it shows full content */
  margin-bottom: 1.5rem;
}

.time-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  width: 100%;
}

.time-card {
  padding: 0.75rem 0.5rem;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  text-align: center;
  font-weight: 700;
  font-size: 0.875rem;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.time-card:hover:not(.disabled),
.time-card:hover:not(.selected) {
  border-color: #d1d5db;
}

.time-card.selected {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.time-card.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Summary Bar */
.summary-bar {
  background: #e7f1ff;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  text-align: center;
  border: 1px solid #b8daff;
  margin-top: 1rem;
}

.summary-label {
  font-size: 1rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.summary-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0056b3;
}

/* Action Buttons Footer */
.action-buttons {
  margin-top: auto;
  flex-shrink: 0;
  padding: 1rem 0;
}

.confirm-btn {
  width: 100%;
  padding: 1rem;
  font-weight: 700;
  font-size: 1.25rem;
  border-radius: 12px;
  border: none;
  background: #111827;
  color: white;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
}

.confirm-btn:hover:not(:disabled) {
  background: #1f2937;
  transform: translateY(-1px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15),
    0 4px 6px -2px rgba(0, 0, 0, 0.1);
}

.confirm-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.confirm-btn.disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
